import dayjs from 'dayjs';
import { NAV_ROUTES } from '@constants/navRoutes';
import { SideNavOptions } from '../components/SideNav/types';
import { ChartDataSet } from '../components/Charts/types';
import { Module, Submodule, SubmoduleFilter } from '../stores/Modules/types/modules';
import { User } from '../stores/User/types/user';
import {
  ApplicationEnvironment,
  DEFAULT_PVX_FINANCING,
  EPSILON,
  GameStatus,
  LOCAL_STORAGE_KEYS,
  MULTIPLICATION_FACTOR_DEFAULT_MONTHLY_REVENUE,
  MULTIPLICATION_FACTOR_DEFAULT_OPERATING_EXPENSES,
  MULTIPLICATION_FACTOR_DEFAULT_PREVIOUS_MONTH_BALANCE,
  SESSION_STORAGE_KEYS,
  SUBMODULE_TYPE,
  SubmoduleCategory,
  USER_ROLES,
  USER_STATUS,
  USER_STATUS_MAP,
} from '../constants/constants';
import { RequestedSubModule, UserSubModulesFilterApiMap } from '../api/userModules';
import { UserSubmoduleMap } from '../api/company';
import {
  CalculatorChartData,
  FundingCalculations,
} from '../stores/FundingCalculator/types/fundingCalculator';
import { MODULES_REQUEST_STATUS } from './types/RequestedDashboards';
import activityTracker from '../services/activityLogger';
import countries from '../constants/countries.json';
import { SuggestionDto } from '@api/types/investor-report';
import Decimal from 'decimal.js';
import { SubmoduleFilters } from '@api/types/filters';

export const SGT_DATE_TIME_FORMATTER = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Asia/Singapore',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hour12: true,
});

export enum SuggestionStatus {
  PENDING = 'PENDING',
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED',
}

export type DecisionEntry = SuggestionDto & { _fromStatus: SuggestionStatus };

export const formatDate = (dateString: string) => {
  if (!dateString) {
    return '-';
  }
  return dayjs(dateString).format('MMM D, YYYY');
};

export const toSentenceCase = (value: string) => {
  const trimmed = value.trim();
  if (!trimmed) return trimmed;
  const lower = trimmed.toLowerCase();
  return `${lower.charAt(0).toUpperCase()}${lower.slice(1)}`;
};

const FINANCIAL_PROJECTIONS_SUBMODULE_TYPE_PRIORITY = [
  SUBMODULE_TYPE.FINANCIAL_PROJECTIONS_V2,
  SUBMODULE_TYPE.FINANCIAL_PROJECTIONS,
] as const;

const getAccessibleFinancialProjectionsSubmodule = (
  modules: Module[],
): { moduleName: string; submoduleUrl: string } | null => {
  if (modules.length === 0 || !modules) {
    return null;
  }
  for (const submoduleType of FINANCIAL_PROJECTIONS_SUBMODULE_TYPE_PRIORITY) {
    for (const module of modules) {
      const matchedSubmodule = module.submodules?.find(
        (submodule) => submodule.submoduleType === submoduleType && Boolean(submodule.url),
      );

      if (matchedSubmodule) {
        return {
          moduleName: createUrlForRequestedSubModule(module),
          submoduleUrl: matchedSubmodule.url,
        };
      }
    }
  }

  return null;
};

export const buildAccessibleFinancialProjectionDashboardUrl = (
  modules: Module[],
  params?: Record<string, string | undefined>,
): string | undefined => {
  const financialProjectionsAccess = getAccessibleFinancialProjectionsSubmodule(modules);

  if (!financialProjectionsAccess) {
    return undefined;
  }

  const searchParams = new URLSearchParams();

  Object.entries(params ?? {}).forEach(([key, value]) => {
    if (value) {
      searchParams.append(key, value);
    }
  });

  const search = searchParams.toString();
  const route = `/${financialProjectionsAccess.moduleName}/${financialProjectionsAccess.submoduleUrl}`;

  return search ? `${route}?${search}` : route;
};

/** Parses `REACT_APP_DUMMY_DEAL_OWNER_ID`; `undefined` if unset, blank, or not a finite number. */
export const getDummyDealOwnerId = (): number | undefined => {
  const raw = process.env.REACT_APP_DUMMY_DEAL_OWNER_ID?.trim();
  if (!raw) return undefined;
  const n = Number(raw);
  return Number.isFinite(n) ? n : undefined;
};

export const formatUtcTimestampToOtherTimezone = (
  isoTimestamp: string,
  formatter: Intl.DateTimeFormat,
  timezone: string | null,
): string => {
  if (!isoTimestamp || !formatter) {
    return '-';
  }

  const date = new Date(isoTimestamp);
  if (Number.isNaN(date.getTime())) {
    return '-';
  }

  const parts = formatter.formatToParts(date);

  let day = '';
  let month = '';
  let year = '';
  let hour = '';
  let minute = '';
  let dayPeriod = '';

  for (const p of parts) {
    if (p.type === 'day') day = p.value;
    else if (p.type === 'month') month = p.value;
    else if (p.type === 'year') year = p.value;
    else if (p.type === 'hour') hour = p.value;
    else if (p.type === 'minute') minute = p.value;
    else if (p.type === 'dayPeriod') dayPeriod = p.value;
  }

  return `${day}-${month}-${year} ${hour}:${minute} ${dayPeriod.toUpperCase()} ${timezone || ''}`;
};

const createUrlForRequestedSubModule = (module: Module): string => {
  const path = module.name.replace(/([A-Z])|\s/g, (match) =>
    match === ' ' ? '_' : match.toLowerCase(),
  );
  return `dashboard/${path}`;
};

const createSubModuleTypeForRequestedSubModule = (module: Module): SUBMODULE_TYPE => {
  return module.name.replace(/\s+/g, '_').toUpperCase() as SUBMODULE_TYPE;
};

export const convertModulesToOptions = (modules: Module[]): SideNavOptions[] => {
  return (
    modules.map((module) => {
      const subOptions = module.submodules?.map((submodule: Submodule) => ({
        label: submodule.name,
        path: submodule.url,
        type: submodule.submoduleType,
      }));

      const metadata = module.metadata ? JSON.parse(module.metadata) : undefined;
      let url = '';
      if (module.requestStatus === 'REQUESTABLE' || module.requestStatus === 'REQUESTED') {
        url = createUrlForRequestedSubModule(module);
      } else if (module.requestStatus === 'APPROVED') {
        url = module.submodules?.[0]?.url || '';
      }
      return {
        label: module.name,
        path: url,
        icon: metadata ? metadata.icon : '',
        metadata: metadata,
        subOptions: subOptions,
        requestStatus: module.requestStatus,
      };
    }) || []
  );
};

export const formatUserList = (users: User[]): User[] => {
  if (users?.length) {
    return users.map((user) => {
      return {
        id: user.id || '',
        email: user.email || '',
        status: USER_STATUS_MAP[user.status as USER_STATUS] || '',
        role: USER_ROLES[user.role as keyof typeof USER_ROLES] || '',
        joiningDate: formatDate(user.joiningDate || ''),
        companyId: user.companyId || '',
      };
    });
  }
  return [];
};

export const formatUserSubmoduleFilters = (
  filtersFromApi: UserSubModulesFilterApiMap[],
): SubmoduleFilter[] => {
  if (filtersFromApi?.length) {
    const submoduleFilters: SubmoduleFilter[] = [];

    filtersFromApi.forEach((submoduleFilter) =>
      submoduleFilter.submoduleFilterRoles.forEach((filterOption) => {
        submoduleFilters.push({
          id: filterOption.id,
          filterValue: filterOption.filterValue,
          submoduleFilterName: submoduleFilter.name,
          submoduleFilterId: submoduleFilter.id,
          isSelected: true,
        });
      }),
    );

    return submoduleFilters;
  }
  return [];
};

export const formatModulesToSubmoduleList = (modules: Module[]): UserSubmoduleMap[] => {
  const submoduleFilters: UserSubmoduleMap[] = [];

  if (modules?.length) {
    modules.forEach((module) => {
      if (module.submodules?.length) {
        module.submodules.forEach((submodule) => {
          submoduleFilters.push({
            moduleId: module.id,
            submoduleId: submodule.id,
            filterIds: submodule.filters?.map((filter) => filter.id) || [],
          });
        });
      }
    });
  }

  return submoduleFilters;
};

export const getSelectedFilterIds = (modules: Module[], submoduleId: string): string[] => {
  const selectedFilterIds: string[] = [];

  for (const module of modules) {
    if (module.submodules) {
      for (const submodule of module.submodules) {
        if (submodule.id === submoduleId) {
          if (submodule.filters) {
            const selectedFilters = submodule.filters.filter((filter) => filter.isSelected);
            selectedFilterIds.push(...selectedFilters.map((filter) => filter.id));
          }
        }
      }
    }
  }

  return selectedFilterIds;
};

export const getDashboardDataBasedOnUrl = (
  modules: Module[],
  url: string,
): {
  id: string;
  name: string;
  embedId: string;
  submoduleType?: SUBMODULE_TYPE;
  filterQuery: string;
  requestStatus: MODULES_REQUEST_STATUS;
  requestId?: string;
  isPreviewEnabled?: boolean;
  submoduleCategory: SubmoduleCategory;
} | null => {
  for (const module of modules) {
    if (module.submodules && module.submodules.length > 0) {
      for (const submodule of module.submodules) {
        if (submodule.url === url) {
          return {
            id: submodule.id || '',
            name: submodule.name,
            embedId: submodule.embedId,
            submoduleType: submodule.submoduleType as SUBMODULE_TYPE,
            requestStatus: 'APPROVED',
            filterQuery: submodule.defaultFilter
              ? `(${submodule.nativeFilterId || 'NATIVE_FILTER-Knb6wV2-t'}:(__cache:(label:'${submodule.defaultFilter}',validateStatus:!f,value:!('${submodule.defaultFilter}')),extraFormData:(filters:!((col:'game_id',op:IN,val:!('${submodule.defaultFilter}')))),filterState:(label:'${submodule.defaultFilter}',validateStatus:!f,value:!('${submodule.defaultFilter}')),id:${submodule.nativeFilterId || 'NATIVE_FILTER-Knb6wV2-t'},ownState:()))`
              : '',
            isPreviewEnabled: submodule.isPreviewEnabled || false,
            submoduleCategory: submodule.submoduleCategory,
          };
        }
      }
    } else if (
      (module.requestStatus === 'REQUESTABLE' || module.requestStatus === 'REQUESTED') &&
      url.slice(1) === createUrlForRequestedSubModule(module)
    ) {
      return {
        id: module.id || '',
        name: module.name,
        embedId: '',
        submoduleType: createSubModuleTypeForRequestedSubModule(module),
        requestStatus: module.requestStatus,
        requestId: module.requestId,
        filterQuery: '',
        submoduleCategory: module.submodules?.[0]?.submoduleCategory || SubmoduleCategory.NATIVE,
      };
    }
  }
  return null;
};

export const toSubmodulePath = (label: string): string => {
  return label.toLowerCase().replace(/ /g, '_').replace(/\(|\)/g, '');
};

export const buildSubmoduleUrl = (label: string, url: string): string => {
  return `${NAV_ROUTES.DASHBOARD}/${toSubmodulePath(label)}/${url}`;
};

export const formatCurrencyWithZero = (
  value: number | string,
  isAbbreviated: boolean = false,
  millionsFractionDigitsCount: number = 0,
  thousandsFractionDigitsCount: number = 0,
  options?: Omit<Intl.NumberFormatOptions, 'maximumFractionDigits' | 'minimumFractionDigits'>,
  showCurrencyCode?: boolean,
): string => {
  if (Number(value) === 0) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      currencyDisplay: showCurrencyCode ? 'code' : 'symbol',
      maximumFractionDigits: isAbbreviated ? 1 : millionsFractionDigitsCount,
      minimumFractionDigits: isAbbreviated ? 1 : millionsFractionDigitsCount,
      ...options,
    }).format(0);
  }

  return formatCurrency(
    value,
    isAbbreviated,
    millionsFractionDigitsCount,
    thousandsFractionDigitsCount,
    options,
    showCurrencyCode,
  );
};

export const formatCurrency = (
  value: number | string,
  isAbbreviated: boolean = false,
  millionsFractionDigitsCount: number = 0,
  thousandsFractionDigitsCount: number = 0,
  options?: Omit<Intl.NumberFormatOptions, 'maximumFractionDigits' | 'minimumFractionDigits'>,
  showCurrencyCode?: boolean,
): string => {
  if (!value || Number(value) === 0) return '';
  const numericValue = Number(value);

  const isMillions = numericValue >= 1000000;

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    currencyDisplay: showCurrencyCode ? 'code' : 'symbol',
    maximumFractionDigits: isMillions
      ? isAbbreviated
        ? 1
        : millionsFractionDigitsCount
      : thousandsFractionDigitsCount,
    minimumFractionDigits: isMillions
      ? isAbbreviated
        ? 1
        : millionsFractionDigitsCount
      : thousandsFractionDigitsCount,
    ...options,

    ...(isAbbreviated ? { notation: 'compact' } : {}),
  }).format(Number(value));
};

export const formatPercentage = (value: number | string | null | undefined): string | null => {
  if (value === null || value === undefined) return null;

  const numeric = typeof value === 'number' ? value : Number(value);
  if (Number.isNaN(numeric)) return null;

  const formatted = (numeric * 100).toFixed(2);
  return `${formatted}%`;
};

export const calculateInterpolatedChartData = (
  monthlyExpenditureData: CalculatorChartData,
): ChartDataSet => {
  if (monthlyExpenditureData.length) {
    const result: CalculatorChartData = [...monthlyExpenditureData];
    const ln = Math.log;

    for (let i = 0; i < monthlyExpenditureData.length - 1; i++) {
      const current = monthlyExpenditureData[i];
      const next = monthlyExpenditureData[i + 1];

      const x = current.value;
      const y = next.value;
      const m = current.month;
      const n = next.month;

      const C = (y - x) / ln(n - m + 1);

      for (let month = m; month <= n; month++) {
        if (!result.some((item) => item.month === month)) {
          const D = month - m + 1;
          const value = x + C * ln(D);
          result.push({ month, value: parseFloat(value.toFixed(4)) });
        }
      }
    }

    result.sort((a, b) => a.month - b.month);

    return [
      {
        label: 'Net Cumulative ROAS',
        data: result.map((res) => ({ x: res.month, y: res.value })),
      },
    ];
  }
  return [];
};

export const convertToFundingCalculations = (data: any): FundingCalculations => {
  const monthlyFinancials = Object.values(data.leadMagnetFinancials.monthlyFinancials);
  const monthlyRoasAndPayback = Object.values(data.leadMagnetRoasAndPayback.monthlyRoasAndPayback);
  const { avgRevenue, avgMarketingSpend, operatingExpenses, latestMonthCashBalance } =
    data.leadMagnetFinancials;

  const calculation = {
    id: data.id || 'playground',
    email: data.email || '',
    requestId: data.requestId || '',
    variables: {
      pvxFinancing: data.leadMagnetFinancials.investorFundingPercentage,
      paybackPeriodInMonths:
        data.leadMagnetFinancials.marketingSpends.totalMonths || monthlyRoasAndPayback.length,
      latestMonthFinancials: {
        netRevenue: avgRevenue,
        marketingSpends: avgMarketingSpend,
        latestMonthCashBalance: latestMonthCashBalance,
        operatingExpenses: operatingExpenses,
        profitBeforeTaxes: avgRevenue - (avgMarketingSpend + operatingExpenses),
      },
      revenueChurnPerMonth: data.leadMagnetFinancials.monthlyRevenueChurn,
      uaIncreasePerMonth: data.leadMagnetFinancials.monthlySpendIncrease,
    },
    summary: {
      cashCost: data.leadMagnetFinancials.cashCost,
      eligibleFund: data.leadMagnetFinancials.marketingSpends.totalInvestorSpend,
      annualFacilitySize: data.leadMagnetFinancials.marketingSpends.totalMarketingSpend,
      totalInvestmentPaybackAmount: monthlyRoasAndPayback.reduce(
        (sum: number, i: any) => sum + i.firstInvestmentPaybackAmount,
        0,
      ),
      totalInvestmentInterestPaid: monthlyRoasAndPayback.reduce(
        (sum: number, i: any) => sum + i.firstInvestmentInterestPaid,
        0,
      ),
    },
    charts: {
      cumulative_roas: {
        datasets: [
          {
            label: 'Net Cumulative ROAS',
            data: monthlyRoasAndPayback.map((i: any, index) => ({
              x: i.month,
              y: i.cumulativeRoas * 100,
            })),
          },
        ],
        title: 'Cumulative ROAS',
        description: 'Net Cumulative Return on Advertising Spend over time.',
      },
      revenue: {
        datasets: [
          {
            label: 'With PvX Funding',
            data: monthlyFinancials.map((i: any, index) => ({
              x: index + 1,
              y: i.netRevenueWithInvestment,
            })),
          },
          // {
          //   label: "Current net revenue",
          //   data: monthlyFinancials.map((i: any, index) => ({ x: index + 1, y: 0 })),
          // }
        ],
        title: 'Revenue Growth',
        description: 'Comparison of revenue growth with PvX Funding and Current Net Revenue.',
      },
      cumulative_revenue: {
        datasets: [
          {
            label: 'With PvX Funding',
            data: monthlyFinancials.map((i: any, index) => ({
              x: index + 1,
              y: i.cumulativeRevenueWithInvestment,
            })),
          },
          // {
          //   label: "Current Net Revenue",
          //   data: monthlyFinancials.map((i: any, index) => ({ x: index + 1, y: 0 })),
          // }
        ],
        title: 'Revenue Growth',
        description: 'Comparison of revenue growth with PvX Funding and Current Net Revenue.',
      },
      monthly_marketing_spends: {
        datasets: [
          {
            label: 'Company Funding',
            data: monthlyFinancials.map((i: any, index) => ({
              x: index + 1,
              y: i.companySpend,
            })),
          },
          {
            label: 'PvX Funding',
            data: monthlyFinancials.map((i: any, index) => ({
              x: index + 1,
              y: i.investorSpend,
            })),
          },
        ],
        title: 'Monthly Marketing Spends',
        description: 'Allocation of marketing spends between PvX Funding and Company Funding.',
      },
      profit_before_taxes: {
        datasets: [
          {
            label: 'With PvX Funding',
            data: monthlyFinancials.map((i: any, index) => ({
              x: index + 1,
              y: i.profitWithInvestment,
            })),
          },
          {
            label: 'Without PvX Funding',
            data: monthlyFinancials.map((i: any, index) => ({
              x: index + 1,
              y: i.profitWithoutInvestment,
            })),
          },
        ],
        title: 'Profit Before Taxes',
        description: 'Profit comparison before taxes with PvX Funding and Current UA Spends.',
      },
      cumulative_profit_before_taxes: {
        datasets: [
          {
            label: 'With PvX Funding',
            data: monthlyFinancials.map((i: any, index) => ({
              x: index + 1,
              y: i.cumulativeProfitWithInvestment,
            })),
          },
          {
            label: 'Without PvX Funding',
            data: monthlyFinancials.map((i: any, index) => ({
              x: index + 1,
              y: i.cumulativeProfitWithoutInvestment,
            })),
          },
        ],
        title: 'Profit Before Taxes',
        description: 'Profit comparison before taxes with PvX Funding and Current UA Spends.',
      },
      ending_cash_balance: {
        datasets: [
          {
            label: 'With PvX Funding',
            data: monthlyFinancials.map((i: any, index) => ({
              x: index + 1,
              y: i.cashBalanceWithInvestment,
            })),
          },
          {
            label: 'Without PvX Funding',
            data: monthlyFinancials.map((i: any, index) => ({
              x: index + 1,
              y: i.cashBalanceWithoutInvestment,
            })),
          },
        ],
        title: 'Cash Balance',
        description: 'Comparison of ending cash balance with PvX Funding and Status Quo.',
      },
      interest_paid: {
        datasets: [
          {
            label: 'Principal Repayment',
            data: monthlyRoasAndPayback.map((i: any, index) => ({
              x: i.month,
              y: i.firstInvestmentPaybackAmount,
            })),
          },
          {
            label: 'Cash cost',
            data: monthlyRoasAndPayback.map((i: any, index) => ({
              x: i.month,
              y: i.firstInvestmentInterestPaid,
            })),
          },
        ],
        title: 'Interest Paid',
        description: 'Comparison of interest paid',
      },
    },
  };

  return calculation;
};

export const getFundingValue = (avgMonthlyMarketingSpend: number, paybackPeriod: number = 12) => {
  // avgMarketingSpend * pvxFundingPercentage * ((1 + monthlyRevenueIncrease)^12 - 1) / monthlyRevenueIncrease
  const value =
    (avgMonthlyMarketingSpend * DEFAULT_PVX_FINANCING * (Math.pow(1 + 0.025, 12) - 1)) / 0.025;
  return `${formatCurrency(value, true, 1, 1)}`;
};

export const percentageDifference = (a: number, b: number) => {
  if (b === 0) {
    return a;
  }
  return ((a - b) / Math.abs(b)) * 100;
};

export const roundToNthPlace = (number: number, place: number) => {
  const factor = Math.pow(10, place);
  return Math.round(number / factor) * factor;
};

export const ceilToNthPlace = (number: number, place: number) => {
  const factor = Math.pow(10, place);
  return Math.ceil(number / factor) * factor;
};

export const getDefaultAvgRevenue = (avgMonthlyMarketingSpend: number) => {
  const avgRevenue = MULTIPLICATION_FACTOR_DEFAULT_MONTHLY_REVENUE * avgMonthlyMarketingSpend;
  return roundToNthPlace(avgRevenue, 2); // rounding to nearest hundreds
};

export const getDefaultOperatingExpenses = (avgMonthlyMarketingSpend: number) => {
  const operatingExpenses =
    MULTIPLICATION_FACTOR_DEFAULT_OPERATING_EXPENSES * avgMonthlyMarketingSpend;
  return roundToNthPlace(operatingExpenses, 2); // rounding to nearest hundreds
};

export const getDefaultPreviousMonthBalance = (avgMonthlyMarketingSpend: number) => {
  const previousMonthBalance =
    MULTIPLICATION_FACTOR_DEFAULT_PREVIOUS_MONTH_BALANCE * avgMonthlyMarketingSpend;
  return roundToNthPlace(previousMonthBalance, 2); // rounding to nearest hundreds
};

export const processRatingString = (ratingValue: string | number | null | undefined): number => {
  if (ratingValue == null) {
    return 0;
  }

  // If already a number, just round it
  if (typeof ratingValue === 'number') {
    return parseFloat(ratingValue.toFixed(1));
  }

  // Remove any non-numeric characters except dot
  const cleanedRating = ratingValue.replace(/[^0-9.]/g, '');

  // Convert to a floating-point number
  const rating = parseFloat(cleanedRating);

  // Ensure valid number and round to one decimal place
  return isNaN(rating) ? 0 : parseFloat(rating.toFixed(1));
};

// If the modules have a metadata property with a sortOrder array, sort the submodules based on the order
export const updateSubmoduleOrdering = (modules: Module[]): void => {
  modules.forEach((module) => {
    if (!module.metadata || !module.submodules) return;

    const metadata = JSON.parse(module.metadata);
    if (!metadata.sortOrder) return;

    const order: Record<string, number> = {};
    for (const [index, subModuleType] of metadata.sortOrder.entries()) {
      order[subModuleType] = index;
    }

    module.submodules?.sort((a, b) => {
      const orderA = order[a.submoduleType || ''];
      const orderB = order[b.submoduleType || ''];

      return orderA - orderB;
    });
  });
};

export const convertRequestedSubModulesToModules = (
  requestedSubModules: RequestedSubModule[],
  authorizedModules: Module[] = [],
): Module[] => {
  if (!requestedSubModules || requestedSubModules.length === 0) {
    return [];
  }

  // Create a Set of existing module names for O(1) lookup
  const existingModuleIds = new Set(authorizedModules.map((module) => module.name));

  return requestedSubModules
    .map((requestedSubModule) => ({
      id: requestedSubModule.submoduleCatalogId, // Using requestId as the module ID
      name: requestedSubModule.moduleName, // Using submoduleName as the module name
      requestStatus: requestedSubModule.status,
      requestId: requestedSubModule.requestId,
      sortOrder: '', // Placeholder for sortOrder, adjust as needed
      metadata: requestedSubModule.moduleMetadata, // Placeholder for metadata, adjust as needed
    }))
    .filter((module) => !existingModuleIds.has(module.name));
};

export const fakeApiCall = async <T>(response: T, timeout: number = 1000): Promise<T> => {
  return new Promise((resolve) => setTimeout(() => resolve(response), timeout));
};

/**
 * Converts a UTC ISO timestamp to a formatted date string in the given timezone.
 * Format: YYYY-MM-DD, hh:mm AM/PM
 *
 * @param isoTimestamp - UTC timestamp string (e.g. "2025-05-05T07:07:49.245827Z")
 * @returns Formatted date string
 */
export function formatTimestamp(isoTimestamp: string): string {
  try {
    const date = new Date(isoTimestamp);

    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    };

    const formatter = new Intl.DateTimeFormat('en-US', options);
    const parts = formatter.formatToParts(date);

    const partMap = Object.fromEntries(parts.map((p) => [p.type, p.value]));

    return `${partMap.year}-${partMap.month}-${partMap.day}, ${partMap.hour}:${partMap.minute} ${partMap.dayPeriod}`;
  } catch {
    return '';
  }
}

export function formatDateToYYYYMMDD(date: Date): string {
  const year = date.getFullYear();
  // Month is 0-indexed in JS, so add 1 and pad with leading zero if needed
  const month = String(date.getMonth() + 1).padStart(2, '0');
  // Day of month also needs padding for single-digit days
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
}

export const constructFetchGamesQueryString = (args: {
  pageNumber?: number;
  pageSize?: number;
  search?: string;
  selectedRefreshFilters?: string[];
  selectedStatusFilter?: GameStatus[];
  selectedCategoryFilter?: string[];
  selectedGenreFilter?: string[];
  selectedDealOwnerFilter?: string[];
  selectedPricingFilter?: string[];
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC';
}): string => {
  const queryParams = [];

  queryParams.push(`sort=${args.sortBy || 'createdAt'}`);
  queryParams.push(`sortOrder=${args.sortOrder || 'DESC'}`);

  if (args.pageNumber || args.pageNumber === 0) {
    queryParams.push(`pageNumber=${args.pageNumber}`);
  }

  if (args.pageSize) {
    queryParams.push(`pageSize=${args.pageSize}`);
  }

  if (args.search) {
    queryParams.push(`search=${args.search}`);
  }

  if (args.selectedRefreshFilters) {
    args.selectedRefreshFilters.forEach((filter) => {
      queryParams.push(`refreshRequired=${filter}`);
    });
  }

  if (args.selectedStatusFilter && args.selectedStatusFilter?.length >= 1) {
    queryParams.push(
      `status=${args.selectedStatusFilter.length === 1 ? args.selectedStatusFilter[0] : args.selectedStatusFilter.join(',')}`,
    );
  }

  if (args.selectedCategoryFilter && args.selectedCategoryFilter.length >= 1) {
    queryParams.push(`category=${args.selectedCategoryFilter.map(encodeURIComponent).join(',')}`);
  }

  if (args.selectedGenreFilter && args.selectedGenreFilter.length >= 1) {
    queryParams.push(`genre=${args.selectedGenreFilter.map(encodeURIComponent).join(',')}`);
  }

  if (args.selectedDealOwnerFilter && args.selectedDealOwnerFilter.length >= 1) {
    queryParams.push(`dealOwner=${args.selectedDealOwnerFilter.map(encodeURIComponent).join(',')}`);
  }

  if (args.selectedPricingFilter && args.selectedPricingFilter.length >= 1) {
    queryParams.push(
      `pricingMethodology=${args.selectedPricingFilter.map(encodeURIComponent).join(',')}`,
    );
  }

  return queryParams.join('&');
};

/** Values supported when building query strings via {@link buildUrlSearchParamsFromRecord}. */
export type UrlSearchParamsRecordValue =
  | string
  | number
  | boolean
  | null
  | Array<string | number | boolean | null | undefined>;

/**
 * Builds a URL search params from a record
 * Empty or whitespace-only keys are skipped.
 * `undefined` and `null` values are skipped (including `null` / `undefined` array entries).
 * Array values produce repeated query keys (e.g. `tag=a&tag=b`).
 * Keys and values are URI-encoded with {@link encodeURIComponent} (query component encoding).
 *
 * @param record - The record to build the URL search params from
 * @returns The URL search params
 */
export function buildUrlSearchParamsFromRecord(
  record: Partial<Record<string, UrlSearchParamsRecordValue | undefined>>,
): URLSearchParams {
  const params = new URLSearchParams();

  for (const [rawKey, value] of Object.entries(record)) {
    const key = rawKey.trim();
    if (!key || value === undefined || value === null) continue;

    if (Array.isArray(value)) {
      for (const item of value) {
        if (item === undefined || item === null) continue;
        params.append(key, String(item));
      }
    } else {
      params.append(key, String(value));
    }
  }

  return params;
}

// Pre-compiled regex for better performance
const DOMAIN_REGEX = /(?:https?:\/\/)?(?:www\.)?(?:[a-zA-Z0-9-]+\.)*([a-zA-Z0-9-]+)\.[a-zA-Z]{2,}/;

// Utility function to extract domain name from URL
export const extractDomainName = (url: string): string => {
  // Fast path: direct regex match (most common case)
  const match = DOMAIN_REGEX.exec(url);
  const domainName = match?.[1] || url;
  // Capitalize first letter
  return domainName.charAt(0).toUpperCase() + domainName.slice(1);
};

export const formatStringToTitleCase = (str: string): string => {
  if (str.length === 0) return '';

  return str[0].toUpperCase() + str.slice(1).toLowerCase();
};

/**
 * Normalises a LinkedIn company URL by appending `/about` if it is missing.
 * Returns the original value when the input is empty/undefined or not a valid LinkedIn company URL.
 *
 * @param url - The LinkedIn URL to normalise
 * @returns The normalised LinkedIn URL with `/about` appended, or the original value
 */
export const normaliseLinkedinUrl = (url: string | undefined): string | undefined => {
  if (!url) return url;

  // Strip a single trailing slash so the check below is simpler
  const trimmed = url.replace(/\/+$/, '');

  // Already ends with /about → nothing to do
  if (trimmed.endsWith('/about')) return trimmed + '/';

  // Matches the base pattern  https://(www.)linkedin.com/company/<name>
  const basePattern = /^https:\/\/(www\.)?linkedin\.com\/company\/[a-zA-Z0-9_-]+$/;
  if (basePattern.test(trimmed)) {
    return trimmed + '/about/';
  }

  // Not a recognised pattern – return as-is
  return url;
};

export const constructLabelForGameFilter = (
  gameId: string,
  dealName?: string,
  gameName?: string,
): string => {
  if (dealName) return `${dealName} - ${gameId}`;
  if (gameName) return `${gameName} - ${gameId}`;

  return `${gameId}`;
};

export const constructLabelForGameDashboardSelection = (
  gameId: string,
  dealName?: string,
  gameName?: string,
): string => {
  if (dealName) return `${dealName}`;
  if (gameName) return `${gameName}`;

  return `${gameId}`;
};

/**
 * Function to get the file extension from a file
 * @param file - The file to get the extension from
 * @returns The file extension
 */
export const getFileExtension = (file: File): string => {
  const extension = file.name.split('.').pop();
  if (!extension) throw new Error('Invalid File Extension');
  return extension;
};

/**
 * Function to rename a file
 * @param file - The file to rename
 * @param newName - The new name of the file
 * @returns The renamed file
 */
export const renameFile = (file: File, newName: string): File => {
  return new File([file], newName, { type: file.type });
};

/**
 * Generic function to extract metadata from any type
 *
 * @param metadata - The metadata to extract
 * @returns The extracted metadata, if not a string, returns an empty object
 */
export const extractMetadata = (metadata: any) => {
  if (typeof metadata !== 'string') {
    return {};
  }

  try {
    return JSON.parse(metadata);
  } catch (error) {
    return {};
  }
};

/**
 * Fetches an image from an S3 signed URL and converts its binary content to a Base64 string.
 *
 * NOTE: This function is intended for execution in a Node.js environment
 * (or similar environment that provides the global Buffer object).
 * If running in a pure browser environment, the conversion step will differ
 * (see alternative approach below).
 *
 * @param signedUrl The pre-signed URL to the S3 object (image).
 * @returns A Promise that resolves to the Base64 encoded string, or null if the request fails.
 */
export async function convertS3ImageToBase64(signedUrl: string): Promise<string | null> {
  try {
    // 1. Fetch the image data
    const response = await fetch(signedUrl);

    if (!response.ok) {
      console.error(`Error fetching image: ${response.status} ${response.statusText}`);
      return null;
    }

    // 2. Get the response body as an ArrayBuffer (raw binary data)
    const arrayBuffer = await response.arrayBuffer();

    // 3. Use the browser-specific conversion utility
    const base64String = await arrayBufferToBase64Browser(arrayBuffer);

    return base64String;
  } catch (error) {
    console.error('An unexpected error occurred during URL to Base64 conversion:', error);
    return null;
  }
}

/**
 * Converts an ArrayBuffer (raw binary data) into a Base64 encoded string
 * using the browser's native FileReader API.
 * * @param arrayBuffer The binary data retrieved from the fetch response.
 * @returns A Promise that resolves to the Base64 string (without the "data:..." prefix).
 */
function arrayBufferToBase64Browser(arrayBuffer: ArrayBuffer): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    // When the file is loaded (read), this event fires.
    reader.onload = () => {
      // The result will be a Data URI string (e.g., "data:image/png;base64,...")
      const dataUrl = reader.result as string;

      // We only want the Base64 part, so we split at the comma and take the second element.
      resolve(dataUrl);
    };

    reader.onerror = reject;

    // 1. Create a Blob from the ArrayBuffer (a file-like object)
    const blob = new Blob([arrayBuffer]);

    // 2. Read the Blob as a Data URL, which performs the Base64 encoding.
    reader.readAsDataURL(blob);
  });
}

export function getCountries(locale: string = 'en'): { label: string; value: string }[] {
  const countryDisplayNames = new Intl.DisplayNames(locale, { type: 'region' });
  return Object.keys(countries).map((country) => {
    return {
      label: countryDisplayNames.of(country) || countries[country as keyof typeof countries],
      value: country,
    };
  });
}

export const convertStringToNumberIfPossible = (value: string): number | null => {
  try {
    return parseFloat(value);
  } catch (error) {
    return null;
  }
};

const getApplicationEnvironment = (): ApplicationEnvironment => {
  const environment = process.env.REACT_APP_ENVIRONMENT as ApplicationEnvironment;
  if (!environment) {
    return ApplicationEnvironment.DEV;
  }

  return environment;
};

export const isDevEnvironment = (): boolean => {
  return getApplicationEnvironment() === ApplicationEnvironment.DEV;
};

export const isProdEnvironment = (): boolean => {
  return getApplicationEnvironment() === ApplicationEnvironment.PROD;
};

export const isDevControlEnabled = (): boolean => {
  if (!isDevEnvironment()) return false;
  return localStorage.getItem(LOCAL_STORAGE_KEYS.DEV_CONTROL_ENABLED) === 'true';
};

export const setDevControlEnabled = (enabled: boolean): void => {
  if (!isDevEnvironment()) return;
  localStorage.setItem(LOCAL_STORAGE_KEYS.DEV_CONTROL_ENABLED, enabled.toString());
};

/**
 * Formats a timestamp to show relative time or absolute date
 * @param timestamp - ISO timestamp string from backend
 * @returns Formatted string showing:
 *   - "Last viewed {n} minute(s) ago" for < 60 minutes
 *   - "Last viewed {n} hour(s) ago" for < 24 hours
 *   - "Last viewed at DD-MM-YYYY" for >= 1 day
 */
export const formatLastViewed = (timestamp: string): string => {
  const now = new Date();
  const viewedDate = new Date(timestamp);
  const diffInMs = now.getTime() - viewedDate.getTime();
  const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
  const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));

  if (diffInMinutes < 1) {
    return 'Last viewed just now';
  } else if (diffInMinutes < 60) {
    return `Last viewed ${diffInMinutes} ${diffInMinutes === 1 ? 'minute' : 'minutes'} ago`;
  } else if (diffInHours < 24) {
    return `Last viewed ${diffInHours} ${diffInHours === 1 ? 'hour' : 'hours'} ago`;
  } else {
    const day = String(viewedDate.getDate()).padStart(2, '0');
    const month = String(viewedDate.getMonth() + 1).padStart(2, '0');
    const year = viewedDate.getFullYear();
    return `Last viewed at ${day}-${month}-${year}`;
  }
};

// TODO: Move this to types file and update imports of this
export interface RowFormatConfig {
  id: string;
  type?: string;
  precision?: number;
}

export const formatValue = (value: any, rowConfig?: RowFormatConfig) => {
  const DEFAULT_PRECISION = 2;

  if (value === null || value === undefined || value === '') {
    return '-';
  }
  const num = Number(value);
  if (isNaN(num)) return value;

  const precision = rowConfig?.precision ?? DEFAULT_PRECISION;

  switch (rowConfig?.type) {
    case 'percentage':
      return `${(num * 100).toFixed(precision)}%`;
    case 'ratio':
      return `${num.toFixed(precision)}`;
    default:
      return num.toFixed(DEFAULT_PRECISION);
  }
};

export const extractAppIdFromUrl = (url: string): string | null => {
  if (!url) return null;

  try {
    if (url.includes('play.google.com')) {
      const parsedUrl = new URL(url);
      return parsedUrl.searchParams.get('id');
    }

    if (url.includes('apps.apple.com')) {
      const match = url.match(/\/id(\d+)/);
      return match ? match[1] : null;
    }
  } catch {
    return null;
  }

  return null;
};

export const prepareAndNavigateForAuthentication = () => {
  // Session expired or token missing: ends the open view and records a non-manual logout
  activityTracker.logout(false);

  // Save the current relative path in session storage with key as origin
  const currentPath = window.location.pathname + window.location.search + window.location.hash;
  const origin = JSON.stringify({ currentPath });
  sessionStorage.setItem(SESSION_STORAGE_KEYS.ORIGIN, origin);

  window.location.href = NAV_ROUTES.AUTH;
};

export const findLastDayOfMonth = (month: string, format: string = 'YYYY-MM-DD') => {
  try {
    const date = dayjs(month);
    return date.endOf('month').format(format);
  } catch (error) {
    console.error('Error finding last day of month:', error);
    return null;
  }
};

/* Validation Functions */
export const isUndefined = (value: unknown): value is undefined => {
  return value === undefined;
};

export const isNull = (value: unknown): value is null => {
  return value === null;
};

export const isNullOrUndefined = (value: unknown): value is null | undefined => {
  return value === null || value === undefined;
};

export const isNullOrUndefinedOrEmpty = (value: unknown): boolean => {
  return isNullOrUndefined(value) || value === '';
};

export const isValidNumber = (val: unknown): val is number =>
  typeof val === 'number' && !Number.isNaN(val);

type TruncatePosition = 'middle' | 'start' | 'end';

interface TruncateOptions {
  maxLength?: number;
  ellipsis?: string;
  position?: TruncatePosition;
}

/**
 * Truncates a string if it exceeds maxLength.
 *
 * - middle: abc...xyz
 * - start: ...xyz
 * - end: abc...
 */
export const truncateString = (
  value: string | null | undefined,
  { maxLength = 20, ellipsis = '...', position = 'middle' }: TruncateOptions = {},
): string => {
  if (!value) return '';
  if (value.length <= maxLength) return value;

  const ellipsisLength = ellipsis.length;
  if (maxLength <= ellipsisLength) {
    return ellipsis.slice(0, maxLength);
  }

  const remainingLength = maxLength - ellipsisLength;

  switch (position) {
    case 'start': {
      return ellipsis + value.slice(value.length - remainingLength);
    }

    case 'end': {
      return value.slice(0, remainingLength) + ellipsis;
    }

    case 'middle':
    default: {
      const front = Math.ceil(remainingLength / 2);
      const back = Math.floor(remainingLength / 2);

      return value.slice(0, front) + ellipsis + value.slice(value.length - back);
    }
  }
};

/* Precision Comparison Helpers */
// Strictly greater than
export const isGreaterThan = (a: number, b: number): boolean => {
  return new Decimal(a).minus(b).greaterThan(EPSILON);
};

// Greater than or equal to
export const isGreaterThanOrEqualTo = (a: number, b: number): boolean => {
  return new Decimal(a).minus(b).greaterThanOrEqualTo(EPSILON.neg());
};

// Strictly less than
export const isLessThan = (a: number, b: number): boolean => {
  return new Decimal(b).minus(a).greaterThan(EPSILON);
};

// Less than or equal to
export const isLessThanOrEqualTo = (a: number, b: number): boolean => {
  return new Decimal(b).minus(a).greaterThanOrEqualTo(EPSILON.neg());
};

// Equal to
export const isEqualTo = (a: number, b: number): boolean => {
  return new Decimal(a).minus(b).abs().lessThanOrEqualTo(EPSILON);
};

// Not equal to
export const isNotEqualTo = (a: number, b: number): boolean => {
  return !isEqualTo(a, b);
};

/* Decimal Comparison Helpers */
export const compareDecimals = (a: number, b: number): boolean => {
  if (typeof a !== 'number' || typeof b !== 'number') return false;

  const decimalA = Decimal(a);
  const decimalB = Decimal(b);

  return decimalA.equals(decimalB);
};

export const convertRadiansToDegrees = (radians: number): number => {
  return Decimal(radians).times(180).dividedBy(Math.PI).toNumber();
};

export const convertDegreesToRadians = (degrees: number): number => {
  return Decimal(degrees).times(Math.PI).dividedBy(180).toNumber();
};

export const convertStringToNumber = (value: string): number | null => {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  try {
    return Number(value);
  } catch (error) {
    return null;
  }
};

export const normalizePvXName = (name: string): string => {
  return name.replace(/\bPVX\b/g, 'PvX');
};

export const wait = (ms: number): Promise<void> =>
  new Promise<void>((resolve) => setTimeout(resolve, ms));

export const hasEmptyFilterValues = (result: SubmoduleFilters): boolean => {
  const filters = Object.values(result);
  return filters.length === 0 || filters.every((filter) => !filter?.values?.length);
};
